import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { storeData } from "../../LocalConnection/LocalConnection";
import {
  login_admin,
  login_admin_google,
  server_post_json,
} from "../../ServiceConnection/serviceconnection";
import Cookies from "js-cookie";
import CryptoJS from "crypto-js";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { Mail, Lock } from "lucide-react";
import leftimg from "../../Assets/loginLeftimg.webp";

const ENCRYPTION_KEY = "LazyX@2025_AdminSecretKey!";

export default function LoginForm() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [shakeEmail, setShakeEmail] = useState(false);
  const [shakePassword, setShakePassword] = useState(false);
  const [shakeForm, setShakeForm] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });

  const [errors, setErrors] = useState({});

  /* ================= ENCRYPT / DECRYPT ================= */
  const encryptData = (data) =>
    CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString();

  const decryptData = (encrypted) => {
    try {
      const bytes = CryptoJS.AES.decrypt(encrypted, ENCRYPTION_KEY);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch {
      return "";
    }
  };

  /* ================= LOAD REMEMBER ME ================= */
  useEffect(() => {
    const e = Cookies.get("remembered_email");
    const p = Cookies.get("remembered_password");

    if (e && p) {
      const email = decryptData(e);
      const password = decryptData(p);

      if (email && password) {
        setFormData({ email, password, rememberMe: true });
      }
    }
  }, []);

  /* ================= SHAKE HELPERS ================= */
  const triggerFormShake = () => {
    setShakeForm(true);
    setTimeout(() => setShakeForm(false), 600);
  };

  /* ================= INPUT CHANGE ================= */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  /* ================= EMAIL / PASSWORD LOGIN ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const fd = new FormData();
      fd.append("email", formData.email);
      fd.append("password", formData.password);

      const res = await server_post_json(login_admin, fd);
      const data = res.data;

      if (!data.success) {
        setErrors({ general: data.message });
        triggerFormShake();
        return;
      }

      storeData("lazyx_admin_token", data.token);
      storeData("lazyx_admin_email", data.admin.email);
      storeData("lazyx_admin_role", data.admin.role);
      storeData("lazyx_admin_id", data.admin.id);

      if (formData.rememberMe) {
        Cookies.set("remembered_email", encryptData(formData.email), { expires: 30 });
        Cookies.set("remembered_password", encryptData(formData.password), { expires: 30 });
      } else {
        Cookies.remove("remembered_email");
        Cookies.remove("remembered_password");
      }

      navigate("/bookings");
    } catch {
      setErrors({ general: "Server error. Please try again." });
      triggerFormShake();
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= GOOGLE LOGIN (UPDATED) ================= */
  const handleGoogleLogin = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);

      const res = await server_post_json(login_admin_google , {
        email: decoded.email,
      });

      const data = res.data;

      if (!data.success) {
        setErrors({ general: data.message });
        triggerFormShake();
        return;
      }

      // ✅ SAME STORAGE AS NORMAL LOGIN
      storeData("lazyx_admin_token", data.token);
      storeData("lazyx_admin_email", data.admin.email);
      storeData("lazyx_admin_role", data.admin.role);
      storeData("lazyx_admin_id", data.admin.id);

      navigate("/bookings");
    } catch (err) {
      setErrors({
        general:
          err?.response?.data?.message ||
          "Google login failed. Please try again.",
      });
      triggerFormShake();
    }
  };

  /* ================= UI ================= */
  return (
    <div className="min-h-screen flex">
      {/* LEFT IMAGE */}
      <div
        className="hidden lg:flex lg:w-3/5 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${leftimg})` }}
      >
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* RIGHT FORM */}
      <div className="w-full lg:w-2/5 flex items-center justify-center bg-gray-50 px-6 py-12">
        <div className={`w-full max-w-md ${shakeForm ? "animate-pulse" : ""}`}>
          <h2 className="text-4xl font-bold text-center mb-6">Admin Login</h2>

          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
              {errors.general}
            </div>
          )}

          {/* GOOGLE LOGIN */}
          <div className="mb-6">
            <GoogleLogin
              onSuccess={handleGoogleLogin}
              onError={() => {
                setErrors({ general: "Google Sign-In failed" });
                triggerFormShake();
              }}
              width="100%"
            />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Email"
              className="w-full p-3 border rounded"
            />

            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              className="w-full p-3 border rounded"
            />

            <button
              disabled={isLoading}
              className="w-full bg-indigo-600 text-white py-3 rounded"
            >
              {isLoading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
